import type { FirebaseClient } from '@akpl/firebase/client';
import {
  type ClosedReason,
  MembersNode,
  PlayerNode,
  type QueueItem,
  QueueNode,
  rtdbPaths,
  SessionMeta,
  TvState,
} from '@akpl/shared';
import { useEffect, useState } from 'react';

import { getFirebase } from '../firebase';

export interface QueueEntry extends QueueItem {
  id: string;
}

export interface SessionLive {
  /** null until the first snapshot arrives, or when the node fails schema validation. */
  meta: SessionMeta | null;
  queue: QueueNode | null;
  /** `queue` flattened and sorted by `order` — what every list in the app renders. */
  items: QueueEntry[];
  player: PlayerNode | null;
  members: MembersNode | null;
  tvState: TvState | null;
  /** `.info/connected`; drives the reconnect banner. */
  connected: boolean;
  /** True until the `meta` listener has produced its first value. */
  loading: boolean;
  /** True when `meta.status` is `closed` (docs/05 §4: toast + redirect to `/guest`). */
  ended: boolean;
  /**
   * Why it closed, straight from `meta.closedReason` (QA 2026-09-15 UX-566) — what the toast
   * and the `/guest` banner say. `null` while the session is live, or when the API wrote none.
   */
  endedReason: ClosedReason | null;
  /**
   * The rules stopped letting this client read the session, which is how a guest learns the room
   * is gone when the host turns a web party back into Solo: `meta` stays `active`, for the host.
   */
  revoked: boolean;
}

const EMPTY: SessionLive = {
  meta: null,
  queue: null,
  items: [],
  player: null,
  members: null,
  tvState: null,
  connected: false,
  loading: true,
  ended: false,
  endedReason: null,
  revoked: false,
};

export function queueEntries(queue: QueueNode | null): QueueEntry[] {
  if (!queue) return [];
  return Object.entries(queue)
    .map(([id, item]) => ({ ...item, id }))
    .sort((a, b) => a.order - b.order);
}

/**
 * Live session state over the RTDB listeners of `@akpl/firebase/client` (docs/05 §1):
 * `meta`, `queue`, `player`, `members` and `tvState`, each validated with its zod schema from
 * `@akpl/shared` so a malformed node renders as "no data" instead of crashing a list.
 *
 * Pass `sid = null` while there is no session — the hook then holds no subscriptions at all.
 */
export function useSession(sid: string | null, client: FirebaseClient | null = null): SessionLive {
  const [state, setState] = useState<SessionLive>(EMPTY);
  // Resetting during render (not in an effect) is what keeps a session switch from painting
  // the previous session's queue for one frame.
  const [subscribedSid, setSubscribedSid] = useState<string | null>(null);
  if (subscribedSid !== sid) {
    setSubscribedSid(sid);
    setState({ ...EMPTY, loading: Boolean(sid) });
  }

  useEffect(() => {
    const fb = client ?? getFirebase();
    if (!sid || !fb) return;

    const unsubscribers = [
      fb.listen(
        rtdbPaths.meta(sid),
        SessionMeta,
        (meta) =>
          setState((prev) => ({
            ...prev,
            meta,
            loading: false,
            ended: meta?.status === 'closed',
            endedReason: meta?.closedReason ?? null,
          })),
        () => setState((prev) => ({ ...prev, loading: false, revoked: true })),
      ),
      fb.listen(rtdbPaths.queue(sid), QueueNode, (queue) =>
        setState((prev) => ({ ...prev, queue, items: queueEntries(queue) })),
      ),
      fb.listen(rtdbPaths.player(sid), PlayerNode, (player) =>
        setState((prev) => ({ ...prev, player })),
      ),
      fb.listen(rtdbPaths.members(sid), MembersNode, (members) =>
        setState((prev) => ({ ...prev, members })),
      ),
      fb.listen(rtdbPaths.tvState(sid), TvState, (tvState) =>
        setState((prev) => ({ ...prev, tvState })),
      ),
      fb.onConnected((connected) => setState((prev) => ({ ...prev, connected }))),
    ];

    return () => {
      for (const off of unsubscribers) off();
    };
  }, [sid, client]);

  return state;
}
